import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { productCategories } from '@/data/products';

export default function ProductsShowcase() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(8);

  const allCategoryLabel = t('allProducts', 'All Products');

  const categoryTabs = useMemo(
    () => [
      { key: 'all', label: allCategoryLabel },
      ...productCategories.map((cat) => ({ key: cat.key, label: cat.name })),
    ],
    [allCategoryLabel],
  );

  const displayedProducts = useMemo(() => {
    if (activeCategory === 'all') {
      return productCategories.flatMap((cat) => cat.products);
    }
    const cat = productCategories.find((c) => c.key === activeCategory);
    return cat ? cat.products : [];
  }, [activeCategory]);

  const visibleProducts = displayedProducts.slice(0, visibleCount);
  const hasMore = visibleCount < displayedProducts.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 8);
  };

  return (
    <section id="products" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">
            {t('productsSectionLabel', 'Product Catalog')}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">
            {t('productsSectionTitle', 'Featured Products from CSSBuy Spreadsheet')}
          </h2>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            {t(
              'productsSectionDesc',
              'Browse our curated selection of products. Click any item to view details and purchase on kaspreadsheet.com.',
            )}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {categoryTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveCategory(tab.key);
                setVisibleCount(8);
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === tab.key
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {visibleProducts.map((product) => (
            <a
              key={product.id}
              href={product.url}
              target="_blank"
              rel="noopener nofollow"
              className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg hover:border-green-200 transition-all duration-300"
            >
              {/* Product Image */}
              <div className="aspect-square bg-gray-50 overflow-hidden relative">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                {/* Category Badge */}
                <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                  {product.category}
                </span>
              </div>

              {/* Product Info */}
              <div className="p-3 md:p-4">
                <h3 className="text-sm font-medium text-gray-900 leading-snug line-clamp-2 mb-2 group-hover:text-green-600 transition-colors">
                  {product.title}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-green-600 font-semibold text-sm">{product.price}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                    ID:{product.id}
                  </span>
                </div>
                {/* Buy Button */}
                <div className="mt-3">
                  <span className="inline-flex items-center justify-center w-full px-3 py-2 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors">
                    {t('buyNow', 'Buy Now')}
                    <i className="ri-external-link-line ml-1" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="text-center mt-10">
            <button
              onClick={handleLoadMore}
              className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              {t('loadMore', 'Load More Products')}
              <i className="ri-arrow-down-line ml-2" />
            </button>
          </div>
        )}

        {/* Total Count */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {t('showingCount', 'Showing {{visible}} of {{total}} products', {
            visible: visibleProducts.length,
            total: displayedProducts.length,
          })}
        </p>
      </div>
    </section>
  );
}